import React from 'react';
import { Modal } from 'antd';
import { IOrderPostData } from '../../../store/types/order';

interface PreviewOrderModalProps {
  visible: boolean;
  onClose: () => void;
  data: Partial<IOrderPostData> & {
    organization_name?: string;
    contact_email?: string;
    phone?: string;
    full_name?: string;
  };
  currentStep?: number;
}

const PreviewOrderModal: React.FC<PreviewOrderModalProps> = ({
  visible,
  onClose,
  data,
  currentStep
}) => {
  const renderField = (label: string, value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;

    return (
      <div className="mb-3">
        <div className="font-semibold text-gray-700">{label}:</div>
        <div className="text-gray-600 ml-2">{value}</div>
      </div>
    );
  };

  const renderArray = (label: string, items: any[]) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-3">
        <div className="font-semibold text-gray-700">{label}:</div>
        <ul className="list-disc ml-6 text-gray-600">
          {items.map((item, index) => (
            <li key={index}>
              {typeof item === 'string'
                ? item
                : item.question || JSON.stringify(item)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Modal
      title="Order Preview"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <div className="p-4">
        <h3 className="text-lg font-bold mb-4 text-gray-800">Order Details</h3>

        {/* Order Data */}
        <div className="mb-6">
          <h4 className="font-bold text-md mb-2 text-blue-700">
            Basic Information
          </h4>
          {renderField('Position', data.position)}
          {renderField('Order Type ID', data.order_type_id)}
          {renderField('Expertise Level ID', data.expertise_level_id)}
          {renderField('Job Type ID', data.job_type_id)}
          {renderField('Country ID', data.country_id)}
          {renderField('State ID', data.state_id)}
        </div>

        {/* Description */}
        {data.description && (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">
              Job Description
            </h4>
            <div
              className="text-gray-600 p-3 bg-gray-50 rounded"
              dangerouslySetInnerHTML={{ __html: data.description }}
            />
          </div>
        )}

        {/* Skills */}
        {(data.soft_skills && data.soft_skills.length > 0) || data.others ? (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">Skills</h4>
            {renderArray('Soft Skills', data.soft_skills || [])}
            {renderField('Other Skills', data.others)}
          </div>
        ) : null}

        {/* Payment & Location */}
        {(data.price_from || data.price_to || data.currency_id) && (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">
              Payment Information
            </h4>
            {renderField('Min Salary', data.price_from)}
            {renderField('Max Salary', data.price_to)}
            {renderField('Currency ID', data.currency_id)}
          </div>
        )}

        {/* Pretest Questions */}
        {data.pretest_questions && data.pretest_questions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">
              Pretest Questions
            </h4>
            {renderField('Duration (hours)', data.pretest_duration_hours)}
            {renderArray(
              'Questions',
              data.pretest_questions.map((q: any) => q.question)
            )}
          </div>
        )}

        {/* Assessment Questions */}
        {data.assessment_questions && data.assessment_questions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">
              Assessment Questions
            </h4>
            {renderField('Duration (hours)', data.assessment_duration_hours)}
            {renderArray(
              'Questions',
              data.assessment_questions.map((q: any) => q.question)
            )}
          </div>
        )}

        {/* Organization Data (for create order) */}
        {(data.organization_name || data.contact_email || data.phone) && (
          <div className="mb-6">
            <h4 className="font-bold text-md mb-2 text-blue-700">
              Organization
            </h4>
            {renderField('Organization Name', data.organization_name)}
            {renderField('Contact Email', data.contact_email)}
            {renderField('Phone', data.phone)}
            {renderField('Full Name', (data as any).full_name)}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PreviewOrderModal;
